import { computed } from 'vue'
import { EventAttendanceModeType, LocationType, AvailabilityType } from '~/lib/utils/jsonLdTypes'
import { generateToccoEventJsonLd, ToccoEventJsonLdData } from '~/lib/utils/generateToccoEventJsonLd'
import { isRegistrationEnabled, getRegistrationUrl } from '~/lib/utils/toccoUtils'
import {
  ToccoSeminarFieldsFragment,
  ToccoWebinarFieldsFragment,
  ToccoModuleFieldsFragment,
  ToccoIntroModuleFieldsFragment,
  ToccoELearningFieldsFragment,
  ToccoCourseFieldsFragment,
  ToccoWebinar,
} from '~/api/graphql/generated'

type ToccoEntity =
  | ToccoSeminarFieldsFragment
  | ToccoWebinarFieldsFragment
  | ToccoModuleFieldsFragment
  | ToccoIntroModuleFieldsFragment
  | ToccoELearningFieldsFragment
  | ToccoCourseFieldsFragment

/**
 * Parse location string into structured address components
 */
function parseLocation(locationStr?: string) {
  if (!locationStr) return null

  const parts = locationStr.split(',').map((part) => part.trim())

  // Extract postal code and city
  const lastPart = parts[parts.length - 1]
  const postalCodeMatch = lastPart.match(/(\d{4})/)
  const postalCode = postalCodeMatch ? postalCodeMatch[1] : undefined
  const city = postalCode ? lastPart.replace(postalCode, '').trim() : lastPart

  return {
    name: parts[0],
    streetAddress: parts.length > 1 ? parts[1] : undefined,
    addressLocality: city,
    postalCode,
  }
}

/**
 * Parse time string if it matches hh.mm – hh:mm format
 * Returns an array with the start and end times or undefined if it doesn't match the format
 */
function parseTimeString(timeStr?: string): string[] | undefined {
  if (!timeStr) return undefined

  // Normalize the input by replacing different dash types and trimming spaces
  const normalizedTimeStr = timeStr.replace('–', '-').replace('—', '-').trim()

  // First handle the case with "Uhr" and additional text
  if (normalizedTimeStr.match(/^\d{1,2}[.:]\d{2}\s*-\s*\d{1,2}[.:]\d{2}\s*Uhr/)) {
    const timeRange = normalizedTimeStr.split('Uhr')[0].trim()
    return timeRange.split('-').map((time) => time.trim())
  }

  // Then handle the basic format
  if (normalizedTimeStr.match(/^\d{1,2}[.:]\d{2}\s*-\s*\d{1,2}[.:]\d{2}/)) {
    return normalizedTimeStr.split('-').map((time) => time.trim())
  }

  return undefined
}

/**
 * Composable for adding schema.org JSON-LD structured data to Tocco entity pages
 */
export const useSchemaToccoBase = (
  entity: ToccoEntity | undefined,
  type: 'seminar' | 'webinar' | 'module' | 'intro_module' | 'e_learning' | 'course',
) => {
  if (!entity) return
  if (!entity.id) return

  const config = useRuntimeConfig()

  const fieldToccoDataJSON = computed(() => JSON.parse(entity.fieldToccoData ?? ''))

  if (!fieldToccoDataJSON.value) return

  // Get time string from the paths.
  const timeStrings = computed(() => {
    const timeStr = parseTimeString(fieldToccoDataJSON.value.paths?.times?.value)
    return timeStr ? timeStr : ['00:00', '23:59']
  })
  const startTimeString = computed(() => timeStrings.value[0].replace('.', ':'))
  const endTimeString = computed(() => timeStrings.value[1].replace('.', ':'))

  // Format dates and times based on entity type
  const startDate = computed(() => {
    let date: string | undefined | null

    // Handle different entity types
    if (type === 'webinar' || type === 'course') {
      // For webinars and courses, use the entity's fieldStartDate
      date =
        type === 'webinar'
          ? (entity as ToccoWebinar).fieldStartDate?.value
          : (entity as ToccoCourseFieldsFragment).fieldStartDate?.value
    } else if (type === 'seminar') {
      // For seminars, use first_course_date from paths but ensure it's in the current year
      const originalDate = fieldToccoDataJSON.value.paths?.first_course_date?.value
      if (originalDate) {
        const currentYear = new Date().getFullYear()
        const [month, day] = originalDate.split('-').slice(1)
        date = `${currentYear}-${month}-${day}`
      }
    } else {
      // For modules, use first_course_date from paths
      date = fieldToccoDataJSON.value.paths?.first_course_date?.value
    }

    // If we have a date and a valid time string, combine them
    if (date && startTimeString.value) {
      // Split the time string into start and end times.
      const startTime = startTimeString.value
      return `${date}T${startTime}`
    }

    // If we only have a date, return it as is
    return date
  })

  const endDate = computed(() => {
    let date: string | undefined | null

    // Handle different entity types
    if (type === 'webinar' || type === 'course') {
      // For webinars and courses, use the entity's fieldEndDate
      date =
        type === 'webinar'
          ? (entity as ToccoWebinar).fieldEndDate?.value
          : (entity as ToccoCourseFieldsFragment).fieldEndDate?.value
    } else if (type === 'seminar') {
      // For seminars, use last_course_date from paths but ensure it's in the current year
      const originalDate = fieldToccoDataJSON.value.paths?.last_course_date?.value
      if (originalDate) {
        const currentYear = new Date().getFullYear()
        const [month, day] = originalDate.split('-').slice(1)
        date = `${currentYear}-${month}-${day}`
      }
    } else {
      // For modules, use last_course_date from paths
      date = fieldToccoDataJSON.value.paths?.last_course_date?.value
    }

    // If we have a date and a valid time string in part 1.
    if (date && endTimeString.value) {
      const endTime = endTimeString.value
      return `${date}T${endTime}`
    }

    // If we only have a date, return it as is
    return date
  })

  // Parse location data
  const location = computed(() => {
    if (type === 'webinar') {
      const registrationEnabled = isRegistrationEnabled(fieldToccoDataJSON.value)
      const registrationUrl = entity.id
        ? getRegistrationUrl(fieldToccoDataJSON.value, entity.id, registrationEnabled)
        : undefined

      return {
        type: LocationType.VirtualLocation,
        url: registrationUrl || undefined,
      }
    }

    const locationData = parseLocation(fieldToccoDataJSON.value.paths?.city?.value)

    // For seminars, if no location data is available, return undefined
    if (type === 'seminar' && !locationData) {
      return undefined
    }

    // For other types, if location data is not available, return a virtual location
    if (!locationData && type !== 'seminar') {
      return {
        type: LocationType.VirtualLocation,
      }
    }

    // At this point we know locationData is not null
    if (locationData) {
      return {
        type: LocationType.Place,
        name: locationData.name,
        address: {
          streetAddress: locationData.streetAddress,
          addressLocality: locationData.addressLocality,
          postalCode: locationData.postalCode,
          addressCountry: 'CH',
        },
      }
    }

    return undefined
  })

  // Create the event data object
  const eventData: ToccoEventJsonLdData = {
    name: entity.title || '',
    startDate: startDate.value || '',
    endDate: endDate.value || '',
    location: location.value,
    description: fieldToccoDataJSON.value.paths?.subtitle?.value,
    eventAttendanceMode: type === 'webinar' ? EventAttendanceModeType.Online : EventAttendanceModeType.Offline,
    offers: {
      price: (() => {
        const price = parseFloat(fieldToccoDataJSON.value.paths?.price?.value || '0')
        const coursePrice = parseFloat(fieldToccoDataJSON.value.paths?.course_price?.value || '0')
        return Math.max(price, coursePrice).toString()
      })(),
      priceCurrency: 'CHF',
      availability: AvailabilityType.InStock,
      url: entity.entityUrl?.path ? `${config.public.frontendBaseUrl}${entity.entityUrl.path}` : '',
    },
  }

  // Generate the JSON-LD and add it to the page head
  const jsonLd = generateToccoEventJsonLd(eventData)

  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(jsonLd),
      },
    ],
  })
}
